import { useCheckAuth } from '@app/auth/hooks/use-check-auth';
import { UserDetailedInfoDTO, UserRoleEnum } from '@app/users/dto';
import { checkSession } from '@auth/proxy-auth';
import { RESOURCES } from '@constants';
import { useTranslations } from '@i18n/use-translations';
import { axiosInstance } from '@providers/data-provider/utils';
import { useGetIdentity } from '@refinedev/core';
import { useQueryClient } from '@tanstack/react-query';
import { App } from 'antd/es';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router';

type ImpersonationContextType = {
  isImpersonating: boolean;
  startImpersonation: (userId: string) => Promise<void>;
  stopImpersonation: () => void;
  isLoading: boolean;
  isFetched: boolean;
  isSuccess: boolean;
  isError: boolean;
};

const ImpersonationContext = createContext<ImpersonationContextType>({
  isImpersonating: false,
  startImpersonation: async (_: string) => new Promise(resolve => resolve()),
  stopImpersonation: () => {},
  isLoading: false,
  isFetched: false,
  isSuccess: false,
  isError: false,
});

export type ImpersonateUserResponseType = UserDetailedInfoDTO & { tokenExpirationTime: string };

type ImpersonationContextProviderProps = PropsWithChildren<{
  /** Called after impersonation starts or stops so the parent can re-fetch user data. */
  onImpersonationChange?: () => Promise<void>;
}>;

const requestStateInitialValue = {
  isLoading: false,
  isFetched: false,
  isSuccess: false,
  isError: false,
};

export const ImpersonationContextProvider = ({
  children,
  onImpersonationChange,
}: ImpersonationContextProviderProps) => {
  const t = useTranslations();
  const { message } = App.useApp();
  const { isPermitted: isAdmin } = useCheckAuth({ roles: [UserRoleEnum.SimAdmin] });
  const queryClient = useQueryClient();

  // Keep a stable ref so callbacks don't need to be in useCallback deps.
  const onImpersonationChangeRef = useRef(onImpersonationChange);
  onImpersonationChangeRef.current = onImpersonationChange;

  const [isImpersonating, setIsImpersonating] = useState(false);
  const [requestState, setRequestState] = useState(requestStateInitialValue);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetState = useCallback(() => setRequestState(requestStateInitialValue), []);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const {
    data: authenticatedUser,
    isFetched: isFetchedAuthenticatedUser,
    isFetching: isFetchingAuthenticatedUser,
    refetch: refetchIdentity,
  } = useGetIdentity();
  const navigate = useNavigate();

  /** Invalidates all cached queries and re-fetches user identity after an impersonation change. */
  const refreshAfterImpersonationChange = useCallback(async () => {
    navigate('/dashboard');
    await refetchIdentity();
    await onImpersonationChangeRef.current?.();
    queryClient.invalidateQueries();
  }, [navigate, refetchIdentity, queryClient]);

  const invalidateImpersonationToken = useCallback(async () => {
    setRequestState(prevState => ({ ...prevState, isLoading: true }));
    try {
      await axiosInstance.delete(`/client-proxy/${RESOURCES.users}/impersonation`);
      setRequestState(prevState => ({ ...prevState, isSuccess: true }));
      return true;
    } catch {
      message.error(t('impersonation.errors.finish'));
      setRequestState(prevState => ({ ...prevState, isError: true }));
      return false;
    } finally {
      setRequestState(prevState => ({ ...prevState, isFetched: true, isLoading: false }));
    }
  }, [message, t]);

  const stopImpersonation = useCallback(async () => {
    const isSuccess = await invalidateImpersonationToken();
    if (isSuccess) {
      clearTimer();
      setIsImpersonating(false);
      resetState();
      refreshAfterImpersonationChange();
    }
  }, [invalidateImpersonationToken, clearTimer, resetState, refreshAfterImpersonationChange]);

  // Use a ref so the timer callback always calls the latest stopImpersonation.
  const stopImpersonationRef = useRef(stopImpersonation);
  stopImpersonationRef.current = stopImpersonation;

  /** Schedule auto-expiry of impersonation based on a unix timestamp (seconds). */
  const scheduleExpiry = useCallback(
    (expiresAtSec: number) => {
      clearTimer();
      const msUntilExpiry = expiresAtSec * 1000 - Date.now();
      if (msUntilExpiry > 0) {
        timerRef.current = setTimeout(() => stopImpersonationRef.current(), msUntilExpiry);
      }
      // If already expired, don't auto-stop — let the server handle it on the next request.
    },
    [clearTimer],
  );

  // On mount: sync with server session to recover impersonation state after page refresh.
  useEffect(() => {
    let cancelled = false;
    checkSession().then(session => {
      if (cancelled) return;
      const active = session?.isImpersonating ?? false;
      setIsImpersonating(active);
      if (active && session?.impersonationExpiresAt !== undefined) {
        scheduleExpiry(session.impersonationExpiresAt);
      }
    });
    return () => {
      cancelled = true;
      clearTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startImpersonationRequest = useCallback(
    async (userId: string): Promise<boolean> => {
      if (!isAdmin) {
        message.error(t('shared.notPermitted'));
        setRequestState(prevState => ({ ...prevState, isError: true }));
        return false;
      }

      setRequestState(prevState => ({ ...prevState, isLoading: true }));
      try {
        const { data: user } = await axiosInstance.post<ImpersonateUserResponseType>(
          `/client-proxy/${RESOURCES.users}/impersonation/${userId}`,
        );

        if (!user) {
          message.error(t('impersonation.errors.general'));
          setRequestState(prevState => ({ ...prevState, isError: true }));
          return false;
        }

        setRequestState(prevState => ({ ...prevState, isSuccess: true }));
        return true;
      } catch {
        message.error(t('impersonation.errors.general'));
        setRequestState(prevState => ({ ...prevState, isError: true }));
        return false;
      } finally {
        setRequestState(prevState => ({ ...prevState, isFetched: true, isLoading: false }));
      }
    },
    [isAdmin, message, t],
  );

  const startImpersonation = useCallback(
    async (userId: string) => {
      if (!userId) return;
      const success = await startImpersonationRequest(userId);
      if (success) {
        setIsImpersonating(true);
        const session = await checkSession();
        if (session?.impersonationExpiresAt !== undefined) {
          scheduleExpiry(session.impersonationExpiresAt);
        }
        refreshAfterImpersonationChange();
      }
    },
    [startImpersonationRequest, scheduleExpiry, refreshAfterImpersonationChange],
  );

  useEffect(() => {
    // Terminate impersonation session if the user is logged out.
    // Skip while the identity is being fetched in the background to avoid false positives.
    if (
      isImpersonating &&
      isFetchedAuthenticatedUser &&
      !isFetchingAuthenticatedUser &&
      !authenticatedUser
    ) {
      stopImpersonation();
    }
  }, [
    isImpersonating,
    isFetchedAuthenticatedUser,
    isFetchingAuthenticatedUser,
    authenticatedUser,
    stopImpersonation,
  ]);

  useEffect(() => {
    if (isImpersonating) return;
    setRequestState({ isFetched: true, isSuccess: true, isLoading: false, isError: false });
  }, [isImpersonating]);

  return (
    <ImpersonationContext.Provider
      value={{
        isImpersonating,
        startImpersonation,
        stopImpersonation,
        ...requestState,
      }}
    >
      {children}
    </ImpersonationContext.Provider>
  );
};

export const useImpersonation = () => useContext(ImpersonationContext);
